// MultiChat — the unified chat surface for HomeAI.
//
// Two modes:
//   • Single  → classic threaded chat, one model at a time. The model
//               can be swapped per message via the composer chip.
//   • Compare → one prompt fans out to 2–4 selected models. Each
//               response streams into its own column. Clicking
//               "Use this answer" promotes the chosen response into the
//               canonical thread so the conversation can continue.
//
// Lovable AI Gateway models go through `ai-route`. BYOK models
// (prefixed `byok:`) go through `byok-chat` using the user's stored key.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { ArrowUp, Square, GitCompare, MessageSquare, Plus, X, Check, Copy, Sparkles, Zap, Gem, ChevronDown, RotateCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  MODELS_BY_CAPABILITY,
  BYOK_MODELS,
  findModel,
  parseByokModelId,
  type ModelOption,
} from "@/lib/aiModels";

type Role = "user" | "assistant";
type Message = {
  id: string;
  role: Role;
  text: string;
  model?: string | null;       // for assistant: which model produced it
  status?: "streaming" | "done" | "error";
  error?: string | null;
  /** When set, this assistant turn is a fan-out across multiple models. */
  candidates?: Candidate[];
};
type Candidate = {
  model: string;
  text: string;
  status: "streaming" | "done" | "error";
  error?: string | null;
  ms?: number;
};

const TIER_ICON = { cheapest: Zap, balanced: Sparkles, premium: Gem } as const;

let mid = 0;
const nextId = () => `m_${Date.now()}_${++mid}`;

/** Build the available model list for the picker, including BYOK options
 * only for providers the user has saved a key for. */
function useAvailableModels() {
  const { user } = useAuth();
  const [byokProviders, setByokProviders] = useState<Set<string>>(new Set());
  useEffect(() => {
    let alive = true;
    if (!user) { setByokProviders(new Set()); return; }
    supabase.from("user_api_keys").select("provider").then(({ data }) => {
      if (!alive || !data) return;
      setByokProviders(new Set(data.map((r) => r.provider)));
    });
    // Refresh when the BYOK sheet closes (best-effort: window event).
    const onChange = () => {
      supabase.from("user_api_keys").select("provider").then(({ data }) => {
        if (!alive || !data) return;
        setByokProviders(new Set(data.map((r) => r.provider)));
      });
    };
    window.addEventListener("byok-keys-changed", onChange);
    return () => { alive = false; window.removeEventListener("byok-keys-changed", onChange); };
  }, [user]);

  return useMemo(() => {
    const lovable = MODELS_BY_CAPABILITY.chat;
    const byok = BYOK_MODELS.filter((m) => byokProviders.has(m.byokProvider!));
    return { lovable, byok, all: [...lovable, ...byok], byokProviders };
  }, [byokProviders]);
}

/** Stream a single chat response. Routes BYOK ids to `byok-chat`, others
 * to `ai-route`. Calls `onDelta` for each token chunk. */
async function streamOne(opts: {
  modelId: string;
  messages: { role: Role; text: string }[];
  signal: AbortSignal;
  onDelta: (chunk: string) => void;
}): Promise<void> {
  const { modelId, messages, signal, onDelta } = opts;
  const { data: { session } } = await supabase.auth.getSession();
  const authHeader = session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
  const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const byok = parseByokModelId(modelId);
  let url: string;
  let body: unknown;
  if (byok) {
    url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/byok-chat`;
    body = {
      provider: byok.provider,
      model: byok.model,
      messages: messages.map((m) => ({ role: m.role, content: m.text })),
    };
  } else {
    // ai-route only takes a single prompt today, so collapse the convo
    // by passing the latest user turn (it doesn't keep a thread). For
    // now we send the last user prompt; a follow-up will widen it.
    url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-route`;
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    body = { prompt: lastUser?.text ?? "", model: modelId };
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey, ...authHeader },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    const t = await res.text().catch(() => "");
    throw new Error(t || `Request failed (${res.status})`);
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() ?? "";
    for (const line of lines) {
      if (!line.startsWith("data:")) continue;
      const payload = line.slice(5).trim();
      if (!payload || payload === "[DONE]") continue;
      try {
        const evt = JSON.parse(payload);
        const delta = evt.choices?.[0]?.delta?.content;
        if (typeof delta === "string" && delta.length) onDelta(delta);
      } catch { /* ignore parse errors on intermediate events */ }
    }
  }
}

export default function MultiChat() {
  const [mode, setMode] = useState<"single" | "compare">("single");
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [singleModel, setSingleModel] = useState<string>("google/gemini-3-flash-preview");
  const [placeholder, setPlaceholder] = useState<string | null>(null);
  const [compareModels, setCompareModels] = useState<string[]>([
    "google/gemini-3-flash-preview",
    "openai/gpt-5-mini",
  ]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [comparePickerOpen, setComparePickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  const { all, lovable, byok } = useAvailableModels();

  // Autosize textarea + autofocus
  useEffect(() => {
    const el = taRef.current; if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
  }, [input]);
  useEffect(() => { taRef.current?.focus(); }, []);
  // Allow other surfaces (suggestion chips, library) to seed the composer.
  useEffect(() => {
    const onSeed = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (typeof detail === "string") {
        setInput(detail);
        setTimeout(() => taRef.current?.focus(), 0);
      }
    };
    window.addEventListener("multichat-seed", onSeed as EventListener);
    const onSetMode = (e: Event) => {
      const detail = (e as CustomEvent<{ modelId?: string; placeholder?: string }>).detail || {};
      if (detail.modelId) setSingleModel(detail.modelId);
      if (detail.placeholder) setPlaceholder(detail.placeholder);
      setMode("single");
      setTimeout(() => taRef.current?.focus(), 0);
    };
    window.addEventListener("multichat-set-mode", onSetMode as EventListener);
    return () => {
      window.removeEventListener("multichat-seed", onSeed as EventListener);
      window.removeEventListener("multichat-set-mode", onSetMode as EventListener);
    };
  }, []);
  useEffect(() => {
    const el = scrollerRef.current; if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Keyboard shortcut: ⌘⇧C toggles mode.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "c") {
        e.preventDefault();
        setMode((m) => (m === "single" ? "compare" : "single"));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const updateMsg = useCallback((id: string, patch: (m: Message) => Message) => {
    setMessages((arr) => arr.map((m) => m.id === id ? patch(m) : m));
  }, []);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setBusy(false);
  }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    const userMsg: Message = { id: nextId(), role: "user", text };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput("");
    setBusy(true);
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    if (mode === "single") {
      const aId = nextId();
      setMessages((arr) => [...arr, { id: aId, role: "assistant", text: "", model: singleModel, status: "streaming" }]);
      try {
        await streamOne({
          modelId: singleModel,
          messages: history.map(({ role, text }) => ({ role, text })),
          signal,
          onDelta: (chunk) => updateMsg(aId, (m) => ({ ...m, text: m.text + chunk })),
        });
        updateMsg(aId, (m) => ({ ...m, status: "done" }));
      } catch (e: any) {
        if (e?.name !== "AbortError") {
          updateMsg(aId, (m) => ({ ...m, status: "error", error: e?.message ?? "Failed" }));
          toast.error(e?.message ?? "Failed");
        }
      } finally {
        setBusy(false);
      }
      return;
    }

    // Compare mode: fan out to all selected models in parallel.
    const aId = nextId();
    const candidates: Candidate[] = compareModels.map((m) => ({ model: m, text: "", status: "streaming" }));
    setMessages((arr) => [...arr, { id: aId, role: "assistant", text: "", status: "streaming", candidates }]);
    const started = compareModels.map(() => performance.now());
    await Promise.all(compareModels.map((modelId, idx) =>
      streamOne({
        modelId,
        messages: history.map(({ role, text }) => ({ role, text })),
        signal,
        onDelta: (chunk) => updateMsg(aId, (m) => ({
          ...m,
          candidates: m.candidates?.map((c, i) => i === idx ? { ...c, text: c.text + chunk } : c),
        })),
      })
        .then(() => updateMsg(aId, (m) => ({
          ...m,
          candidates: m.candidates?.map((c, i) => i === idx ? { ...c, status: "done", ms: Math.round(performance.now() - started[idx]) } : c),
        })))
        .catch((e) => {
          if (e?.name === "AbortError") return;
          updateMsg(aId, (m) => ({
            ...m,
            candidates: m.candidates?.map((c, i) => i === idx ? { ...c, status: "error", error: e?.message ?? "Failed" } : c),
          }));
        }),
    ));
    updateMsg(aId, (m) => ({ ...m, status: "done" }));
    setBusy(false);
  }, [input, busy, messages, mode, singleModel, compareModels, updateMsg]);

  // Promote a compare candidate into the canonical thread answer.
  const useCandidate = useCallback((messageId: string, modelId: string) => {
    setMessages((arr) => arr.map((m) => {
      if (m.id !== messageId || !m.candidates) return m;
      const chosen = m.candidates.find((c) => c.model === modelId);
      if (!chosen) return m;
      return { ...m, text: chosen.text, model: chosen.model, candidates: undefined, status: "done" };
    }));
    // Drop back into single mode using the winning model so the convo continues.
    setSingleModel(modelId);
    setMode("single");
    toast.success(`Continuing with ${findModel(modelId)?.label ?? modelId}`);
  }, []);

  const reset = () => { stop(); setMessages([]); };

  const toggleCompareModel = (id: string) => {
    setCompareModels((arr) => {
      if (arr.includes(id)) return arr.length > 1 ? arr.filter((m) => m !== id) : arr;
      if (arr.length >= 4) { toast.error("Max 4 models in compare"); return arr; }
      return [...arr, id];
    });
  };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !busy) send();
    }
  };

  return (
    <div className="w-full">
      {/* Mode toggle */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card/60 p-0.5">
          <button
            type="button"
            onClick={() => setMode("single")}
            className={cn("h-7 px-3 inline-flex items-center gap-1.5 rounded-full text-[11px] font-mono transition-colors",
              mode === "single" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}
          >
            <MessageSquare className="w-3 h-3" /> Single
          </button>
          <button
            type="button"
            onClick={() => setMode("compare")}
            className={cn("h-7 px-3 inline-flex items-center gap-1.5 rounded-full text-[11px] font-mono transition-colors",
              mode === "compare" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}
            title="⌘⇧C"
          >
            <GitCompare className="w-3 h-3" /> Compare
            <span className="hidden sm:inline text-[9px] opacity-70 ml-1">{compareModels.length}</span>
          </button>
        </div>
        {messages.length > 0 && (
          <button
            type="button"
            onClick={reset}
            className="h-7 px-2 inline-flex items-center gap-1 rounded-md text-[10px] font-mono text-muted-foreground hover:text-foreground hover:bg-secondary/60"
          >
            <RotateCw className="w-3 h-3" /> New chat
          </button>
        )}
      </div>

      {/* Transcript */}
      {messages.length > 0 && (
        <div ref={scrollerRef} className="mb-3 max-h-[55vh] overflow-y-auto rounded-2xl border border-border bg-card/40 p-3 space-y-4">
          {messages.map((m) => (
            <MessageRow key={m.id} message={m} onUseCandidate={useCandidate} />
          ))}
        </div>
      )}

      {/* Composer */}
      <div className={cn(
        "relative w-full rounded-3xl border bg-card/60 backdrop-blur transition-all",
        busy ? "border-primary/40" : "border-border focus-within:border-primary/60 focus-within:shadow-[0_0_40px_hsl(var(--primary)/0.25)]",
      )}>
        <textarea
          ref={taRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          rows={1}
          placeholder={mode === "compare" ? "Ask everyone at once. Compare what they say." : (placeholder ?? "Ask anything. Build anything.")}
          className="w-full resize-none bg-transparent px-5 pt-4 pb-14 text-base text-foreground placeholder:text-muted-foreground/70 outline-none"
        />
        <div className="absolute left-3 bottom-3 right-3 flex items-center justify-between gap-2">
          {/* Model controls */}
          {mode === "single" ? (
            <SingleModelChip
              value={singleModel}
              onChange={setSingleModel}
              open={pickerOpen}
              setOpen={setPickerOpen}
              lovable={lovable}
              byok={byok}
            />
          ) : (
            <CompareModelChips
              selected={compareModels}
              onToggle={toggleCompareModel}
              open={comparePickerOpen}
              setOpen={setComparePickerOpen}
              lovable={lovable}
              byok={byok}
            />
          )}
          <div className="flex items-center gap-2">
            {busy ? (
              <button
                type="button"
                onClick={stop}
                aria-label="Stop"
                className="h-9 w-9 grid place-items-center rounded-full bg-secondary text-foreground hover:bg-secondary/80"
              >
                <Square className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={send}
                disabled={!input.trim()}
                aria-label="Send"
                className={cn(
                  "h-9 w-9 grid place-items-center rounded-full transition-all",
                  input.trim()
                    ? "bg-primary text-primary-foreground hover:scale-105 shadow-[0_0_20px_hsl(var(--primary)/0.4)]"
                    : "bg-secondary text-muted-foreground cursor-not-allowed",
                )}
              >
                <ArrowUp className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Hint row */}
      <div className="mt-2 px-1 flex items-center justify-between text-[10px] font-mono text-muted-foreground/70">
        <span>
          {mode === "compare"
            ? `≈ ${compareModels.length} models in parallel · uses your credits per response`
            : "One model per message — swap any time. ⌘⇧C to compare."}
        </span>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new Event("open-byok-keys"))}
          className="underline-offset-2 hover:underline"
        >
          + Add your AI keys
        </button>
      </div>
    </div>
  );
}

// --- Subcomponents -----------------------------------------------------------

function ModelTierIcon({ model }: { model: ModelOption | null }) {
  if (!model) return null;
  const I = TIER_ICON[model.tier];
  return <I className="w-3 h-3 text-primary" />;
}

function SingleModelChip(props: {
  value: string;
  onChange: (v: string) => void;
  open: boolean;
  setOpen: (v: boolean) => void;
  lovable: ModelOption[];
  byok: ModelOption[];
}) {
  const { value, onChange, open, setOpen, lovable, byok } = props;
  const selected = findModel(value) ?? lovable[0];
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border border-border/70 bg-card/60 hover:border-primary/50 text-[11px] font-mono"
      >
        <ModelTierIcon model={selected} />
        <span className="text-foreground/90 truncate max-w-[180px]">{selected.label}</span>
        <ChevronDown className="w-3 h-3 text-muted-foreground" />
      </button>
      {open && (
        <ModelMenu
          lovable={lovable}
          byok={byok}
          onPick={(id) => { onChange(id); setOpen(false); }}
          isSelected={(id) => id === value}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function CompareModelChips(props: {
  selected: string[];
  onToggle: (id: string) => void;
  open: boolean;
  setOpen: (v: boolean) => void;
  lovable: ModelOption[];
  byok: ModelOption[];
}) {
  const { selected, onToggle, open, setOpen, lovable, byok } = props;
  return (
    <div className="relative flex items-center gap-1 flex-wrap min-w-0">
      {selected.map((id) => {
        const m = findModel(id);
        return (
          <span key={id} className="inline-flex items-center gap-1 h-7 pl-2 pr-1 rounded-full border border-border bg-card/60 text-[11px] font-mono">
            <ModelTierIcon model={m} />
            <span className="truncate max-w-[120px]">{m?.label ?? id}</span>
            <button
              type="button"
              onClick={() => onToggle(id)}
              className="h-5 w-5 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary/70"
              aria-label="Remove"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        );
      })}
      {selected.length < 4 && (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-full border border-dashed border-border text-muted-foreground hover:text-foreground hover:border-primary/50 text-[11px] font-mono"
        >
          <Plus className="w-3 h-3" /> Add model
        </button>
      )}
      {open && (
        <ModelMenu
          lovable={lovable}
          byok={byok}
          onPick={(id) => { onToggle(id); }}
          isSelected={(id) => selected.includes(id)}
          onClose={() => setOpen(false)}
          multi
        />
      )}
    </div>
  );
}

function ModelMenu(props: {
  lovable: ModelOption[];
  byok: ModelOption[];
  onPick: (id: string) => void;
  isSelected: (id: string) => boolean;
  onClose: () => void;
  multi?: boolean;
}) {
  const { lovable, byok, onPick, isSelected, onClose, multi } = props;
  return (
    <>
      <button className="fixed inset-0 z-30" aria-hidden onClick={onClose} />
      <div className="absolute z-40 mt-2 bottom-full mb-2 left-0 w-80 max-h-[60vh] overflow-y-auto rounded-xl border border-border bg-popover text-popover-foreground shadow-xl p-1.5">
        <Section title="Lovable AI · included">
          {lovable.map((m) => (
            <ModelRow key={m.id} m={m} selected={isSelected(m.id)} onClick={() => onPick(m.id)} multi={multi} />
          ))}
        </Section>
        <Section title="Your keys · BYOK">
          {byok.length === 0 ? (
            <button
              type="button"
              onClick={() => { onClose(); window.dispatchEvent(new Event("open-byok-keys")); }}
              className="w-full text-left px-2 py-2 rounded-lg text-xs font-mono text-muted-foreground hover:bg-secondary/60"
            >
              + Add OpenAI / Anthropic / OpenRouter keys to unlock more models
            </button>
          ) : byok.map((m) => (
            <ModelRow key={m.id} m={m} selected={isSelected(m.id)} onClick={() => onPick(m.id)} multi={multi} />
          ))}
        </Section>
      </div>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-1">
      <div className="px-2 py-1 text-[9px] uppercase tracking-wider font-mono text-muted-foreground/70">{title}</div>
      {children}
    </div>
  );
}

function ModelRow({ m, selected, onClick, multi }: { m: ModelOption; selected: boolean; onClick: () => void; multi?: boolean }) {
  const I = TIER_ICON[m.tier];
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full flex items-start gap-2 px-2 py-2 rounded-lg text-left hover:bg-secondary/60 transition-colors",
        selected && "bg-secondary/70",
      )}
    >
      <I className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-mono truncate">{m.label}</span>
          <span className="text-[10px] font-mono text-muted-foreground shrink-0">
            {m.pricePer1M > 0 ? `$${m.pricePer1M.toFixed(2)}/1M` : "—"}
          </span>
        </div>
        <div className="text-[10px] text-muted-foreground truncate">{m.blurb}</div>
      </div>
      {multi && selected && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
    </button>
  );
}

function MessageRow({ message, onUseCandidate }: { message: Message; onUseCandidate: (mid: string, modelId: string) => void }) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl bg-primary text-primary-foreground px-4 py-2 text-sm whitespace-pre-wrap">
          {message.text}
        </div>
      </div>
    );
  }
  // Assistant — single answer
  if (!message.candidates) {
    const m = message.model ? findModel(message.model) : null;
    return (
      <div className="space-y-1">
        <div className="prose prose-sm dark:prose-invert max-w-none">
          {message.text ? <ReactMarkdown>{message.text}</ReactMarkdown> : <span className="text-muted-foreground text-xs">…</span>}
        </div>
        <div className="flex items-center gap-2">
          {m && (
            <span className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground">
              <ModelTierIcon model={m} /> {m.label}
            </span>
          )}
          {message.status === "error" && (
            <span className="text-[10px] text-destructive font-mono">{message.error}</span>
          )}
          {message.text && (
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(message.text); toast.success("Copied"); }}
              className="text-[10px] font-mono text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          )}
        </div>
      </div>
    );
  }
  // Assistant — compare candidates
  return (
    <div>
      <div className="text-[10px] font-mono text-muted-foreground mb-1.5">Compare · {message.candidates.length} models</div>
      <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 xl:grid-cols-2">
        {message.candidates.map((c) => {
          const m = findModel(c.model);
          return (
            <div key={c.model} className="rounded-xl border border-border bg-background/60 p-3 flex flex-col min-h-[160px]">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="inline-flex items-center gap-1 text-[11px] font-mono">
                  <ModelTierIcon model={m} /> {m?.label ?? c.model}
                </span>
                <span className="text-[10px] font-mono text-muted-foreground">
                  {c.status === "streaming" ? "…" : c.ms ? `${(c.ms / 1000).toFixed(1)}s` : ""}
                </span>
              </div>
              <div className="prose prose-sm dark:prose-invert max-w-none flex-1 overflow-hidden">
                {c.status === "error" ? (
                  <p className="text-destructive text-xs">{c.error}</p>
                ) : c.text ? (
                  <ReactMarkdown>{c.text}</ReactMarkdown>
                ) : (
                  <p className="text-muted-foreground text-xs">Streaming…</p>
                )}
              </div>
              <div className="flex items-center justify-end gap-1 pt-2 mt-2 border-t border-border">
                <button
                  type="button"
                  onClick={() => { navigator.clipboard.writeText(c.text); toast.success("Copied"); }}
                  className="h-7 px-2 inline-flex items-center gap-1 rounded-md text-[10px] font-mono text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  disabled={!c.text}
                >
                  <Copy className="w-3 h-3" /> Copy
                </button>
                <button
                  type="button"
                  onClick={() => onUseCandidate(message.id, c.model)}
                  disabled={c.status !== "done" || !c.text}
                  className={cn(
                    "h-7 px-2 inline-flex items-center gap-1 rounded-md text-[10px] font-mono",
                    c.status === "done" && c.text
                      ? "bg-primary text-primary-foreground hover:opacity-90"
                      : "bg-secondary text-muted-foreground cursor-not-allowed",
                  )}
                >
                  <Check className="w-3 h-3" /> Use this
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}